import { useMutation } from "@apollo/client";
import Edit from "@mui/icons-material/Edit";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { RightDrawerShell } from "components/RightDrawerShell/RightDrawerShell";
import { StatusChip } from "components/StatusChip";
import {
  StateWasteStreamRegistrationQuery,
  UpdateStateWasteStreamRegistrationInput,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link as RouterLink } from "react-router-dom";
import { prettyPrintDateMed } from "util/dates";
import { buildWasteStreamUrl } from "util/wasteUrls";

import { UPDATE_STATE_WASTE_STREAM_REGISTRATION, useStateWasteStreamRegistration } from "./api";
import { RegistrationFormFields, RegistrationFormValues } from "./RegistrationForm";
import { StatusBadge } from "./StatusBadge";
import { STATUS_SELECT_ITEMS } from "./statusSelectItems";

type RegistrationDetail = NonNullable<
  StateWasteStreamRegistrationQuery["stateWasteStreamRegistration"]
>;

export type RegistrationDetailDrawerProps = {
  registrationId: string | null;
  onClose: () => void;
  refetchQueries?: string[];
  /**
   * Staff cross-tenant surface: renders `{tenantName} · {facility} · {state}`
   * plus a new-tab link to the registration's facility waste page. When false,
   * `{facility} · {state}` is shown — the within-tenant customer surface
   * already knows which org it's in.
   */
  showTenantContext?: boolean;
};

function toFormValues(registration: RegistrationDetail): RegistrationFormValues {
  return {
    // The edit surface doesn't use the facility field (the header covers
    // that context); the shared form shape carries it so create and this
    // drawer's edit surface can share RegistrationFormFields.
    facility: null,
    stateRegisteredId: registration.stateRegisteredId ?? "",
    profileNumber: registration.profileNumber ?? "",
    status: registration.status,
    name: registration.name ?? "",
    companyDescription: registration.companyDescription ?? "",
    federalWasteCodes: registration.federalWasteCodes,
    formCodes: registration.formCodes,
    sourceCode: registration.sourceCode ?? "",
    dotId: registration.dotId ?? "",
    dotDescription: registration.dotDescription ?? "",
    registeredAt: registration.registeredAt ?? null,
    closedAt: registration.closedAt ?? null,
  };
}

// Placeholder defaults for before the query resolves. `reset` swaps these out
// for the real record's values the instant it loads.
const EMPTY_FORM_VALUES: RegistrationFormValues = {
  facility: null,
  stateRegisteredId: "",
  profileNumber: "",
  status: STATUS_SELECT_ITEMS[0].value,
  name: "",
  companyDescription: "",
  federalWasteCodes: [],
  formCodes: [],
  sourceCode: "",
  dotId: "",
  dotDescription: "",
  registeredAt: null,
  closedAt: null,
};

function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

/**
 * Read-with-edit-pencil drawer for a single state waste stream registration —
 * shared by the staff registrations grid and (a later task) the customer
 * waste-streams grid, mirroring the waste stream detail modal's view/edit
 * pattern. Only staff see the edit pencil; everyone else gets the read-only
 * view.
 */
export function RegistrationDetailDrawer({
  registrationId,
  onClose,
  refetchQueries,
  showTenantContext = false,
}: RegistrationDetailDrawerProps) {
  const open = registrationId != null;
  const { isStaff } = useCurrentUser();
  const alerts = useAlerts();
  const [editing, setEditing] = useState(false);

  const { data, loading } = useStateWasteStreamRegistration(registrationId);
  const registration = data?.stateWasteStreamRegistration ?? null;

  const { control, handleSubmit, reset, formState } = useForm<RegistrationFormValues>({
    defaultValues: EMPTY_FORM_VALUES,
  });

  // A different record (or the drawer closing) resets the form to that
  // record's own values, establishing a fresh dirty-tracking baseline.
  useEffect(() => {
    reset(registration ? toFormValues(registration) : EMPTY_FORM_VALUES);
  }, [registration, reset]);

  // Closing always lands back on the read-only view for next time.
  useEffect(() => {
    if (!open) setEditing(false);
  }, [open]);

  const [updateRegistration, { loading: saving }] = useMutation(
    UPDATE_STATE_WASTE_STREAM_REGISTRATION,
    { refetchQueries, awaitRefetchQueries: true }
  );

  const handleClose = () => {
    setEditing(false);
    onClose();
  };

  const handleCancelEdit = () => {
    if (registration) reset(toFormValues(registration));
    setEditing(false);
  };

  const onSubmit = async (formValues: RegistrationFormValues) => {
    if (!registration) return;
    // Only the fields the user actually touched are sent to the update
    // mutation (react-hook-form dirtyFields) — a partial update leaves
    // everything else untouched server-side.
    const dirty = formState.dirtyFields;
    const input: UpdateStateWasteStreamRegistrationInput = { id: registration.id };
    if (dirty.stateRegisteredId)
      input.stateRegisteredId = formValues.stateRegisteredId.trim() || null;
    if (dirty.profileNumber) input.profileNumber = formValues.profileNumber.trim() || null;
    if (dirty.status) input.status = formValues.status;
    if (dirty.name) input.name = formValues.name.trim() || null;
    if (dirty.companyDescription)
      input.companyDescription = formValues.companyDescription.trim() || null;
    if (dirty.federalWasteCodes) input.federalWasteCodes = formValues.federalWasteCodes;
    if (dirty.formCodes) input.formCodes = formValues.formCodes;
    if (dirty.sourceCode) input.sourceCode = formValues.sourceCode.trim() || null;
    if (dirty.dotId) input.dotId = formValues.dotId.trim() || null;
    if (dirty.dotDescription) input.dotDescription = formValues.dotDescription.trim() || null;
    if (dirty.registeredAt) input.registeredAt = formValues.registeredAt;
    if (dirty.closedAt) input.closedAt = formValues.closedAt;

    try {
      await updateRegistration({ variables: { input } });
      alerts.success("Registration updated.");
      setEditing(false);
    } catch (e) {
      alerts.error("Failed to update the registration.", e);
    }
  };

  const title = registration
    ? registration.name || registration.facility?.name || registration.state
    : "";

  // The edit pencil lives in the shell's header-actions slot (beside Close)
  // rather than inline after the title, so a long registration name can't push
  // it off the edge — it stays visible while the title truncates.
  const headerActions =
    registration && isStaff && !editing ? (
      <Tooltip title="Edit registration">
        <IconButton
          size="small"
          onClick={() => setEditing(true)}
          aria-label="Edit registration"
          sx={{ color: "warning.main" }}
        >
          <Edit fontSize="small" />
        </IconButton>
      </Tooltip>
    ) : undefined;

  const footer = editing ? (
    <Stack direction="row" spacing={1} justifyContent="flex-end">
      <Button onClick={handleCancelEdit} disabled={saving}>
        Cancel
      </Button>
      <LoadingButton
        variant="contained"
        loading={saving}
        disabled={saving || !formState.isDirty}
        onClick={handleSubmit(onSubmit)}
      >
        Save
      </LoadingButton>
    </Stack>
  ) : null;

  return (
    <RightDrawerShell
      open={open}
      onClose={handleClose}
      title={title}
      headerActions={headerActions}
      footer={footer}
    >
      {loading && !registration && (
        <Stack spacing={2}>
          <Skeleton variant="text" width={240} height={32} />
          <Skeleton variant="rectangular" height={120} />
          <Skeleton variant="rectangular" height={160} />
        </Stack>
      )}
      {registration &&
        (editing ? (
          <Stack spacing={2}>
            <RegistrationContextHeader
              registration={registration}
              showTenantContext={showTenantContext}
            />
            <Typography variant="body2" color="text.secondary">
              This affects {pluralize(registration.linkedWasteStreamCount, "linked stream")}.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Authoritative federal waste code changes should flow through a state update submission
              — only edit them here to correct a mistake.
            </Typography>
            <RegistrationFormFields control={control} disabled={saving} />
          </Stack>
        ) : (
          <RegistrationDetailView
            registration={registration}
            showTenantContext={showTenantContext}
          />
        ))}
    </RightDrawerShell>
  );
}

/**
 * The tenant / facility / state context line shown at the top of the drawer,
 * in both the read-only view and the edit form so the facility is always in
 * sight. Staff (showTenantContext) also get the cross-tenant "Open facility"
 * link; the within-tenant customer surface omits it.
 */
function RegistrationContextHeader({
  registration,
  showTenantContext,
}: {
  registration: RegistrationDetail;
  showTenantContext: boolean;
}) {
  const facilityName = registration.facility?.name ?? "—";
  if (!showTenantContext) {
    return (
      <Typography variant="body2" color="text.secondary">
        {facilityName} · {registration.state}
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2" color="text.secondary">
        {registration.tenantName} · {facilityName} · {registration.state}
      </Typography>
      <Link
        component={RouterLink}
        to={`/o/${registration.tenantId}/waste/facilities/${registration.facilityId}`}
        target="_blank"
        rel="noopener noreferrer"
        variant="body2"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
      >
        <OpenInNew fontSize="small" />
        Open facility
      </Link>
    </Stack>
  );
}

function RegistrationDetailView({
  registration,
  showTenantContext,
}: {
  registration: RegistrationDetail;
  showTenantContext: boolean;
}) {
  return (
    <Stack spacing={3}>
      <RegistrationContextHeader
        registration={registration}
        showTenantContext={showTenantContext}
      />

      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={1}>
          <StatusBadge status={registration.status} />
          <Typography variant="body2" color="text.secondary">
            Registered Date:{" "}
            {registration.registeredAt ? prettyPrintDateMed(registration.registeredAt) : "—"} ·
            Closed Date: {registration.closedAt ? prettyPrintDateMed(registration.closedAt) : "—"}
          </Typography>
        </Stack>
        <Typography variant="body2">
          State Registered ID: {registration.stateRegisteredId || "—"}
        </Typography>
      </Stack>

      <Box>
        <Typography variant="subtitle2">Federal Waste Codes</Typography>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
          Authoritative — used for matching
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {registration.federalWasteCodes.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ) : (
            registration.federalWasteCodes.map((code) => (
              <Chip key={code} label={code} size="small" />
            ))
          )}
        </Stack>
      </Box>

      <Box>
        <Typography variant="subtitle2">Descriptive — not used for matching</Typography>
        <Stack spacing={0.5} sx={{ mt: 1 }}>
          <Typography variant="body2">
            Profile Number: {registration.profileNumber || "—"}
          </Typography>
          <Typography variant="body2">Name: {registration.name || "—"}</Typography>
          <Typography variant="body2">
            Company Description: {registration.companyDescription || "—"}
          </Typography>
          <Typography variant="body2">
            Form Codes: {registration.formCodes.join(", ") || "—"}
          </Typography>
          <Typography variant="body2">Source Code: {registration.sourceCode || "—"}</Typography>
          <Typography variant="body2">DOT ID: {registration.dotId || "—"}</Typography>
          <Typography variant="body2">
            DOT Description: {registration.dotDescription || "—"}
          </Typography>
        </Stack>
      </Box>

      <Box>
        <Typography variant="subtitle2">
          Linked Encamp Waste Streams ({registration.linkedWasteStreamCount})
        </Typography>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {registration.linkedWasteStreams.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No linked waste streams.
            </Typography>
          )}
          {registration.linkedWasteStreams.map((stream) => (
            <Box
              key={stream.id}
              sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Link
                  component={RouterLink}
                  to={buildWasteStreamUrl(registration.tenantId, stream.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, minWidth: 0 }}
                >
                  <OpenInNew fontSize="small" />
                  <span
                    style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {stream.name || stream.id}
                  </span>
                </Link>
                {stream.outdated && (
                  <Tooltip
                    title={`Missing from the registration: ${stream.missingFederalWasteCodes.join(", ")}`}
                  >
                    <StatusChip
                      color="warning"
                      variant="outlined"
                      label="Update needed"
                      sx={{ flexShrink: 0 }}
                    />
                  </Tooltip>
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {stream.federalWasteCodes.join(", ") || "—"}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
}
